import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { trackFeatureUsage, trackEvent } from '../utils/analytics';

const AttendanceScanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [cameraPermission, setCameraPermission] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  useEffect(() => {
    trackFeatureUsage('Attendance Scanner', 'view');
    
    // Check if user is SYSTEM_CUSTOMER
    const isCustomer = user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER');
    if (!isCustomer) {
      setError('Only customers can access the attendance scanner');
      return;
    }

    requestCameraPermission();
  }, [user]);

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment' // Use back camera if available
        } 
      });
      setCameraPermission('granted');
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera permission denied:', error);
      setCameraPermission('denied');
      setError('Camera access is required to scan QR codes. Please enable camera permission and refresh the page.');
    }
  };

  const startScanning = () => {
    if (!videoRef.current) return;
    
    setScanning(true);
    setError('');
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    const scanFrame = () => {
      if (!scanning) return;
      
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        // Simple QR detection - in a real implementation, you'd use a QR library
        // For now, we'll provide manual entry as the primary method
        
        // Continue scanning
        requestAnimationFrame(scanFrame);
      } else {
        requestAnimationFrame(scanFrame);
      }
    };
    
    scanFrame();
  };

  const stopScanning = () => {
    setScanning(false);
    
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) {
      setError('Please enter the QR code');
      return;
    }

    // Extract UUID from URL or use as-is
    let uuid = manualCode.trim();
    if (uuid.includes('/mark-attendance/')) {
      uuid = uuid.split('/mark-attendance/')[1];
    }
    
    trackEvent('attendance_manual_code_entered', {
      event_category: 'Attendance',
      event_label: 'Manual Code Entry'
    });
    
    navigate(`/mark-attendance/${uuid}`);
  };

  const handleQrDetected = (detectedUuid) => {
    stopScanning();
    
    trackEvent('attendance_qr_detected', {
      event_category: 'Attendance',
      event_label: 'QR Code Detected'
    });
    
    navigate(`/mark-attendance/${detectedUuid}`);
  };

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  return (
    <div className="page">
      <h1 className="page-title">Scan Attendance QR</h1>
      
      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
            📱 QR Code Scanner
          </h3>
          <p style={{ color: 'var(--gray-dark)' }}>
            Scan the attendance QR code displayed at your club
          </p>
        </div>

        {cameraPermission === 'denied' ? (
          <div style={{
            background: '#FED7D7',
            border: '1px solid #FEB2B2',
            padding: '20px',
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📷</div>
            <h4 style={{ color: '#C53030', marginBottom: '10px' }}>
              Camera Access Required
            </h4>
            <p style={{ color: '#C53030', marginBottom: '15px' }}>
              Please enable camera permission to scan QR codes
            </p>
            <button
              className="btn btn-primary"
              onClick={requestCameraPermission}
            >
              Enable Camera
            </button>
          </div>
        ) : cameraPermission === 'granted' ? (
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{
              position: 'relative',
              display: 'inline-block',
              border: '3px solid var(--primary-blue)',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto'
                }}
              />
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              />
              
              {/* Scanning overlay */}
              {scanning && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '200px',
                  height: '200px',
                  border: '3px solid #48BB78',
                  borderRadius: '12px',
                  background: 'rgba(72, 187, 120, 0.1)'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#48BB78',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '15px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    Scanning...
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ marginTop: '20px' }}>
              {!scanning ? (
                <button
                  className="btn btn-primary"
                  onClick={startScanning}
                  style={{
                    background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                    border: 'none',
                    fontSize: '16px',
                    padding: '12px 24px'
                  }}
                >
                  📷 Start Scanning
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  onClick={stopScanning}
                >
                  ⏹️ Stop Scanning
                </button>
              )}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📷</div>
            <p style={{ color: 'var(--gray-dark)' }}>
              Requesting camera permission...
            </p>
          </div>
        )}

        {/* Manual Entry Section */}
        <div style={{
          borderTop: '2px solid var(--gray-medium)',
          paddingTop: '30px',
          textAlign: 'center'
        }}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowManualEntry(!showManualEntry)}
            style={{ marginBottom: '20px' }}
          >
            {showManualEntry ? '📷 Use Camera Instead' : '⌨️ Enter Code Manually'}
          </button>

          {showManualEntry && (
            <div>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                Manual Code Entry
              </h4>
              <form onSubmit={handleManualSubmit} style={{ maxWidth: '400px', margin: '0 auto' }}>
                <div className="form-group">
                  <label htmlFor="manualCode" className="form-label">
                    Enter QR Code or URL
                  </label>
                  <input
                    type="text"
                    id="manualCode"
                    className="form-input"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Paste QR code URL or UUID here"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  Submit Code
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div style={{
          background: 'var(--pale-blue)',
          padding: '20px',
          borderRadius: '8px',
          marginTop: '30px'
        }}>
          <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
            📋 How to use:
          </h4>
          <ul style={{ 
            marginLeft: '20px', 
            color: 'var(--gray-dark)',
            lineHeight: '1.6',
            textAlign: 'left'
          }}>
            <li>Point your camera at the QR code displayed at your club</li>
            <li>Make sure the QR code is clearly visible in the scanning area</li>
            <li>The app will automatically detect and process the code</li>
            <li>If camera doesn't work, use manual entry option</li>
            <li>You can mark attendance only once per day</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AttendanceScanner;
